import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { debounce, isEqual } from "lodash"
import type { ReactElement } from "react"
import React from "react"

import clsxm from "@/lib/clsxm"
import type { Geolocation } from "@/lib/geolocation"

import Icon from "@/components/common/Icon/Icon"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"

import { LocationIcon } from "@/constants/icons"

export type BookingSearchDropdownProps = {
  children: React.ReactNode
  contentProps?: DropdownMenu.DropdownMenuRootContentProps
  fromGivenScreenWidth: boolean
  geolocation: Geolocation.ContextData
  onFocus?: React.FocusEventHandler<HTMLDivElement>
  onLocationResolve: (args: {
    address: Geolocation.Bing.Address
    position: Geolocation.Position | void
  }) => Promise<void> | void
  open: boolean
  textInput?: string
}

type State = Record<string, never>

export class BookingSearchDropdownComponent extends React.Component<
  BookingSearchDropdownProps,
  State
> {
  private debouncedProps: BookingSearchDropdownProps = this.props

  public componentDidUpdate = async () => {
    await this.syncGeolocationSuggestions()
  }

  private handleKeyDown = (event: React.KeyboardEvent) => {
    if ([`Enter`, ` `].includes(event.key)) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  private onUseMyLocationClick = async () => {
    const { error, result } = await this.props.geolocation.useMyLocation()

    if (error) {
      console.error(error)
      return
    }

    const { address, position } = result || {}

    if (address) {
      await this.props.onLocationResolve({ address, position })
    }
  }

  public render = (): ReactElement => {
    return (
      <DropdownMenu.Root modal={false} open={true}>
        <DropdownMenu.Trigger
          asChild
          className={`focus:outline-none focus-visible:outline-none`}
          onKeyDown={this.handleKeyDown}
        >
          {this.props.children}
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          {...this.props.contentProps}
          align={this.props.fromGivenScreenWidth ? `end` : `start`}
          aria-disabled={!open}
          className={clsxm(
            `flex flex-col divide-y rounded-b-xl border-t bg-white tracking-tight shadow-md transition last:overflow-hidden`,
            this.props.open ? `visible` : `invisible h-0`,
            this.props.contentProps?.className
          )}
          id={`dropdown`}
          onFocus={this.props.onFocus}
          side={`bottom`}
        >
          {this.props.geolocation.processing ? (
            <LoadingSpinner expanded />
          ) : (
            <>
              <DropdownMenu.Item
                asChild
                className={`cursor-pointer px-3 py-2 transition hover:bg-gray-50 focus-visible:outline-none`}
                onClick={this.onUseMyLocationClick}
              >
                <span className={`flex flex-row items-center space-x-1`}>
                  <Icon size={`sm-20`} icon={LocationIcon} />
                  <p>Use my Location</p>
                </span>
              </DropdownMenu.Item>
              {this.props.geolocation.suggestions.length > 0
                ? this.props.geolocation.suggestions.map(
                    (suggestion, index) => {
                      return (
                        <DropdownMenu.Item
                          key={index}
                          onClick={async () => {
                            const { address, position } =
                              await this.props.geolocation.chooseSuggestion({
                                suggestion,
                              })

                            if (address) {
                              await this.props.onLocationResolve({
                                address,
                                position,
                              })
                            }
                          }}
                          className={`cursor-pointer px-3 py-2 transition hover:bg-gray-50`}
                        >
                          <span>{suggestion.formattedAddress}</span>
                        </DropdownMenu.Item>
                      )
                    }
                  )
                : null}
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    )
  }

  private syncGeolocationSuggestions = debounce(async () => {
    if (this.props.textInput === this.debouncedProps.textInput) {
      return
    }

    this.debouncedProps = {
      ...this.debouncedProps,
      textInput: this.props.textInput,
    }

    try {
      const trimmedInput = this.props.textInput?.trim()
      if (!trimmedInput) {
        this.props.geolocation.reset()
        return
      }

      await this.props.geolocation.fetchSuggestions({ input: trimmedInput })
    } catch (error) {
      console.error(error)
    }
  }, 500)

  public shouldComponentUpdate = (
    nextProps: Readonly<BookingSearchDropdownProps>,
    nextState: Readonly<State>
  ) => {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState)
  }
}
